import { ROLES } from './roleHelpers';

export const ARCHIVE_EXTENSIONS = ['rar', 'zip', 'tar', 'gz', 'tgz', '7z'];

export const BASE_ALLOWED_EXTENSIONS = [
  // Office
  'doc', 'docx', 'docm', 'dot', 'dotx',
  'xls', 'xlsx', 'xlsm', 'xlt', 'xltx',
  'ppt', 'pptx', 'pptm', 'pps', 'ppsx', 'pot', 'potx',
  'odt', 'ods', 'odp',
  // Documents
  'pdf', 'txt', 'rtf',
  // Images
  'gif', 'png', 'svg', 'svgz', 'jpg', 'jpeg', 'jpe', 'webp',
  // Audio / video
  'mp3', 'mp4', 'aif', 'aiff', 'aac', 'wav', 'mpeg', 'mpg', 'avi',
];

export const GUEST_LIMITS = {
  maxFiles: 5,
  maxTotalBytes: 25 * 1024 * 1024,
};

export const COMPANY_LIMITS = {
  maxFiles: 10,
  maxTotalBytes: 100 * 1024 * 1024,
};

export function isGuestUser(userType) {
  return userType === ROLES.GUEST_USER;
}

export function getTicketFileLimits(userType) {
  return isGuestUser(userType) ? GUEST_LIMITS : COMPANY_LIMITS;
}

export function getAllowedExtensions(userType) {
  const extensions = [...BASE_ALLOWED_EXTENSIONS];
  if (!isGuestUser(userType)) {
    extensions.push(...ARCHIVE_EXTENSIONS);
  }
  return extensions;
}

export function getAcceptAttribute(userType) {
  return getAllowedExtensions(userType).map((ext) => `.${ext}`).join(',');
}

export function getFileExtension(fileName) {
  if (!fileName || !fileName.includes('.')) return '';
  return fileName.split('.').pop().toLowerCase();
}

export function isArchiveExtension(extension) {
  return ARCHIVE_EXTENSIONS.includes(extension);
}

export function formatMegabytes(bytes) {
  return Math.round(bytes / (1024 * 1024));
}

export function getLimitsHint(userType) {
  const limits = getTicketFileLimits(userType);
  const maxMb = formatMegabytes(limits.maxTotalBytes);
  if (isGuestUser(userType)) {
    return `حداکثر ${limits.maxFiles} فایل، مجموع حجم تا ${maxMb} مگابایت (فایل فشرده مجاز نیست)`;
  }
  return `حداکثر ${limits.maxFiles} فایل، مجموع حجم تا ${maxMb} مگابایت`;
}

function getEntrySize(entry) {
  if (!entry) return 0;
  if (typeof entry.size === 'number') return entry.size;
  if (entry instanceof File) return entry.size;
  if (entry.data_url) {
    const base64 = entry.data_url.split(';base64,')[1] || '';
    const padding = (base64.match(/=+$/) || [''])[0].length;
    return Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
  }
  return 0;
}

export function getExistingTotalBytes(existingFiles = []) {
  return existingFiles.reduce((sum, file) => sum + getEntrySize(file), 0);
}

// Returns null when valid, otherwise a Persian error message.
export function validateTicketFile(file, userType, existingFiles = []) {
  if (!file) return null;

  const limits = getTicketFileLimits(userType);
  const extension = getFileExtension(file.name);
  const allowedExtensions = getAllowedExtensions(userType);

  if (!extension) {
    return 'نام فایل باید دارای پسوند باشد';
  }

  if (isGuestUser(userType) && isArchiveExtension(extension)) {
    return 'فایل‌های فشرده (zip، rar، tar) برای کاربران مهمان مجاز نیست';
  }

  if (!allowedExtensions.includes(extension)) {
    return `فرمت فایل مجاز نیست. فرمت‌های مجاز: ${allowedExtensions.join(', ')}`;
  }

  if (existingFiles.length >= limits.maxFiles) {
    return `حداکثر ${limits.maxFiles} فایل می‌توانید پیوست کنید`;
  }

  const totalBytes = getExistingTotalBytes(existingFiles) + file.size;
  if (totalBytes > limits.maxTotalBytes) {
    const maxMb = formatMegabytes(limits.maxTotalBytes);
    return `مجموع حجم فایل‌ها نباید بیشتر از ${maxMb} مگابایت باشد`;
  }

  return null;
}
